/**
 * Privates erkennen — EINE Liste für Gesundheit, Familie/Beziehung, Geld und Namen. (17.10.2026, Prüfauflage 2+3 zur Selbstverbesserung)
 *
 * Gebraucht, wo Text aus dem Betrieb (Ticket-Titel, Postfach-Zeilen) an ein Modell geht oder seine Ausgabe gespeichert wird:
 * ein Treffer verwirft den ganzen Eintrag (server/selbstverbesserung-quellen.ts, shared/selbstverbesserung.ts). Bewusst grob —
 * lieber ein Technik-Beleg zu wenig als ein privater zu viel.
 *
 * Muster: ein Stamm am Wortanfang (davor kein Buchstabe — `\b` kennt keine Umlaute); Wörter in `GANZ` nur als ganzes Wort
 * (sonst träfe „ehe“ das „eher“). Dazu Anrede mit Namen („Dr. Huber“, „Frau Maier“) und Dosisangaben („20 mg“).
 *
 * ⚠️ Keine Wörter, die im Code-Alltag ständig vorkommen (Konto, Steuer[zeichen], Trennung, Biografie als Funktion).
 */

/** Die Gesundheits-Stämme — auch einzeln gebraucht (shared/privat-transport.ts: nichts davon ins Postfach des Entwicklers). */
export const PRIVAT_GESUNDHEIT: readonly string[] = [
  "arzt", "ärzt", "aerzt", "psych", "therap", "klinik", "krankenhaus", "spital", "krank", "diagnos", "medikament", "tablette",
  "dosis", "ritalin", "antidepress", "depress", "panik", "trauma", "suchtkrank", "alkohol", "schlafmittel", "gesundheit", "blutdruck",
  "herzinfarkt", "schmerz", "schwanger", "betrunk", "adhs", "adhd",
];

/** Stämme — Treffer am Wortanfang, Wortteile danach erlaubt („Arzttermin“, „Scheidungsanwalt“). */
export const PRIVAT_STAEMME: readonly string[] = [
  // Gesundheit
  ...PRIVAT_GESUNDHEIT,
  // Familie, Beziehung, Leben
  "tochter", "töchter", "sohn", "söhne", "kinder", "mutter", "vater", "mama", "papa", "eltern", "bruder", "brüder", "schwester",
  "ehefrau", "ehemann", "freundin", "beziehung", "scheidung", "geschieden", "hochzeit", "unterhalt", "sorgerecht",
  "anwalt", "anwält", "tagebuch", "trauer", "gestorben", "beerdigung", "begräbnis", "verliebt", "liebeskummer", "kindheit", "affäre",
  // Geld (Lohn und Gehalt: `LOHN_GEHALT_QUELLE`)
  "schulden", "kredit", "finanz", "miete", "geld", "iban",
];

/** Nur als ganzes Wort. */
/** „op“ (Operation) nur als ganzes Wort — „Opacity“, „Option“, „OpenAI“ bleiben frei. */
export const PRIVAT_GANZ: readonly string[] = ["ehe", "kind", "sex", "tod", "oma", "opa", "op"];

/**
 * Lohn und Gehalt — die EINE Definition für Übergabe-Filter (server/dreier-uebergabe.ts), Adressat/Faden (server/adressat-jev.ts) und `nenntPrivates`.
 * Als Quelltext (ganze Wörter, mit eigener Wortgrenze), damit jede Liste sie in ihren Ausdruck einsetzen kann; Flags `iu`.
 * Trifft jedes Wort, das mit „Lohn“ beginnt (Lohnzettel, Lohnsteuer), auf „-lohn“ endet (Stunden-, Mindest-, Nettolohn) oder „löhn“ enthält (Löhne),
 * und jedes Wort mit „gehalt“ (nicht vor „en“) oder „gehälter“ (Gehalt, Gehalts…, Gehaltabrechnung, gehaltvoll, Gehälter, Monats-, Bruttogehalt).
 * Frei bleiben das Verb „lohnen“ in seinen echten Formen (lohnt, lohnte(n), lohnen, lohnend…, lohnenswert…), „belohnt“, „gelohnt“, „eingehalten“, „festgehalten“.
 * LOCKERUNG: „gehalten“ (gehaltene/-n/-r/-s) WIRD frei — in Übergabe-Liste und `nenntPrivates` war es bisher über den Präfix „gehalt“ gesperrt.
 * Dadurch bleibt auch die seltene Schreibung „Gehaltentwicklung“ (ohne Fugen-s) frei.
 * In Kauf genommene Fehlalarme: Informations-, Wahrheits-, Fett- und Wassergehalt; in Adressat/Faden und `nenntPrivates` auch die seltenen Verbformen
 * lohne, lohnst, lohnet, lohnendste. Nicht erfasst: andere Schreibweisen (Umschrift „Loehne“, zerlegte Umlaute) und „lohn“ mitten im Wort (Mindestlohngesetz).
 */
const LOHN_VERB = String.raw`(?:t(?:en?)?|en(?:(?:d|swert)(?:e[mnrs]?)?)?)`;
export const LOHN_GEHALT_QUELLE = String.raw`(?<!\p{L})(?:\p{L}*l(?:ohn(?!\p{L})|öhn\p{L}*)|lohn(?!${LOHN_VERB}(?!\p{L}))\p{L}*|\p{L}*geh(?:alt(?!en)|älter)\p{L}*)(?!\p{L})`;

const esc = (w: string): string => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Groß-/Kleinschreibung zählt nur für die Anrede-Muster — darum dort ein eigener Lauf ohne `i`. */
const ANREDE = /(?<!\p{L})(?:[Dd]r|[Pp]rof)\.?\s+\p{Lu}|(?<!\p{L})(?:[Ff]rau|[Hh]err|[Hh]errn)\s+\p{Lu}/u;

const MUSTER_OHNE_ANREDE = new RegExp(
  [
    ...PRIVAT_STAEMME.map((w) => `(?<!\\p{L})${esc(w)}`),
    ...PRIVAT_GANZ.map((w) => `(?<!\\p{L})${esc(w)}(?!\\p{L})`),
    LOHN_GEHALT_QUELLE,
    "\\d+\\s?(?:mg|ml|µg)(?!\\p{L})",
  ].join("|"),
  "iu",
);

/** Nennt der Text etwas Privates? Rein. */
export function nenntPrivates(text: string): boolean {
  const t = text ?? "";
  if (ANREDE.test(t)) return true;
  // Stämme, ganze Wörter, Dosis — ohne Groß/Klein; die Anrede oben mit (sonst träfe „frau“ jedes folgende Wort).
  return MUSTER_OHNE_ANREDE.test(t);
}

const GESUNDHEIT = new RegExp([...PRIVAT_GESUNDHEIT.map((w) => `(?<!\\p{L})${esc(w)}`), "\\d+\\s?(?:mg|ml|µg)(?!\\p{L})"].join("|"), "iu");

/** Nennt der Text etwas zur Gesundheit (Stämme oben, Dosisangaben)? Rein. */
export function nenntGesundheit(text: string): boolean {
  return gesundheitTreffer(text) !== null;
}

/** Der auslösende Stamm (klein) — oder null. Rein. */
export function gesundheitTreffer(text: string): string | null {
  return GESUNDHEIT.exec(text ?? "")?.[0].toLowerCase() ?? null;
}
